import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthProvider";
import { apiGet, apiPost, apiPut, apiDelete } from "../lib/api-client";
import ConfirmDialog from "./ConfirmDialog";
import OuraConnectButton, { WipWearableConnectors } from "./OuraConnectButton";
import InviteToAppButton from "./InviteToAppButton";
import ConsentBadge from "./ConsentBadge";
import {
  ListPatientsResp,
  Patient,
  CreatePatientResp,
  UpdatePatientResp,
  DeletePatientResp,
} from "../types";
import "./PatientsPanel.css";

type FormState = {
  patient_id?: string;
  name: string;
  dob: string;
  email: string;
  phone: string;
  notes: string;
  billing_name: string;
  billing_zip: string;
  billing_city: string;
  billing_address: string;
  billing_tax_number: string;
};

const emptyForm: FormState = {
  name: "", dob: "", email: "", phone: "", notes: "",
  billing_name: "", billing_zip: "", billing_city: "", billing_address: "",
  billing_tax_number: "",
};

const PatientsPanel: React.FC<{ onLoadPatient?: (patientId: string, name: string) => void }> = ({ onLoadPatient }) => {
  const { t } = useTranslation();
  const { ensureToken } = useAuth();

  // ---- FIX: keep ensureToken reference stable to avoid effect loops ----
  const ensureTokenRef = useRef(ensureToken);
  useEffect(() => {
    ensureTokenRef.current = ensureToken;
  }, [ensureToken]);

  // Stable token getter (never re-created due to deps)
  const getTokenOrThrow = useCallback(async (): Promise<string> => {
    const t = await ensureTokenRef.current();
    if (!t) throw new Error("No auth token");
    return t;
  }, []);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm); // only for EDIT
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  // NEW: modal state
  const [showNewModal, setShowNewModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Patient | null>(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return !f
      ? patients
      : patients.filter(
          (p) =>
            (p.name || "").toLowerCase().includes(f) ||
            (p.email || "").toLowerCase().includes(f) ||
            (p.phone || "").toLowerCase().includes(f)
        );
  }, [patients, filter]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await apiGet<ListPatientsResp>("/patients", getTokenOrThrow);
      if (!mountedRef.current) return;
      setPatients(r.items || []);
    } catch (e: any) {
      if (!mountedRef.current) return;
      setErr(e?.message || String(e));
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
    }
  }, [getTokenOrThrow]);

  useEffect(() => {
    void load();
  }, [load]);

  // NEW button – only opens modal; does not touch EDIT form
  const startCreate = () => setShowNewModal(true);

  // EDIT on the right panel
  const startEdit = (p: Patient) =>
    setForm({
      patient_id: p.patient_id,
      name: p.name || "",
      dob: p.dob || "",
      email: p.email || "",
      phone: p.phone || "",
      notes: p.notes || "",
      billing_name: p.billing_name || "",
      billing_zip: p.billing_zip || "",
      billing_city: p.billing_city || "",
      billing_address: p.billing_address || "",
      billing_tax_number: p.billing_tax_number || "",
    });

  // Create from modal
  const createPatient = async (data: Omit<FormState, "patient_id">) => {
    setSaving(true);
    setErr(null);
    try {
      const r = await apiPost<CreatePatientResp>("/patients", data, getTokenOrThrow);
      setPatients((ps) => [r.patient, ...ps]);
      setShowNewModal(false); // close modal
      // leave EDIT panel as-is
    } catch (e: any) {
      setErr(e?.message || String(e));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // Save from EDIT form
  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.patient_id) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await apiPut<UpdatePatientResp>(
        `/patients/${form.patient_id}`,
        {
          name: form.name, dob: form.dob, email: form.email, phone: form.phone,
          notes: form.notes,
          billing_name: form.billing_name, billing_zip: form.billing_zip,
          billing_city: form.billing_city, billing_address: form.billing_address,
          billing_tax_number: form.billing_tax_number,
        },
        getTokenOrThrow
      );
      setPatients((ps) => ps.map((p) => (p.patient_id === r.patient.patient_id ? r.patient : p)));
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiDelete<DeletePatientResp>(`/patients/${deleteTarget.patient_id}`, getTokenOrThrow);
      setPatients((ps) => ps.filter((x) => x.patient_id !== deleteTarget.patient_id));
      if (form.patient_id === deleteTarget.patient_id) setForm(emptyForm);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <>
      {/* two-column only when there is an EDIT detail */}
      <div className={`patientsLayout ${form.patient_id ? "hasDetail" : ""}`}>
        {/* LEFT – list */}
        <section className="card">
          <header className="cardHeader">
            <div className="titleRow">
              <h2>{t("patients.title")}</h2>
              <div className="toolbar">
                <div className="searchWrap">
                  <input
                    className="input"
                    placeholder={t("patients.searchPlaceholder")}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  <span className="kbd">/</span>
                </div>
                <button className="btn btn--primary" onClick={startCreate}>+ {t("patients.new")}</button>
              </div>
            </div>
            {err && <div className="error">{err}</div>}
          </header>

          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: "28%" }}>{t("patients.name")}</th>
                  <th style={{ width: "28%" }}>{t("patients.email")}</th>
                  <th style={{ width: "18%" }}>{t("patients.phone")}</th>
                  <th style={{ width: "14%" }}>{t("patients.dob")}</th>
                  <th style={{ width: "12%" }}>{t("patients.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`skel-${i}`} className="skeletonRow"><td colSpan={5} /></tr>
                  ))}
                {!loading &&
                  filtered.map((p) => (
                    <tr key={p.patient_id}>
                      <td className="truncate">{p.name}</td>
                      <td className="truncate">{p.email}</td>
                      <td className="truncate">{p.phone}</td>
                      <td className="truncate">{p.dob}</td>
                      <td className="actions">
                        <button className="chip" onClick={() => startEdit(p)}>{t("common.edit")}</button>
                        <button className="chip danger" onClick={() => setDeleteTarget(p)}>{t("common.delete")}</button>
                      </td>
                    </tr>
                  ))}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">{t("patients.noMatch")}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* RIGHT – only when editing */}
        {form.patient_id && (
          <section className="card">
            <header className="cardHeader">
              <h2>{t("patients.editPatient")}</h2>
            </header>

            <form onSubmit={saveEdit} className="formGrid">
              <label className="field">
                <span>{t("patients.name")}</span>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </label>
              <label className="field">
                <span>{t("patients.email")}</span>
                <input
                  className="input"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  type="email"
                />
              </label>
              <label className="field">
                <span>{t("patients.phone")}</span>
                <input
                  className="input"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </label>
              <label className="field">
                <span>{t("patients.dob")}</span>
                <input
                  className="input"
                  value={form.dob}
                  onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))}
                  placeholder={t("patients.dobPlaceholder")}
                />
              </label>
              <label className="field colSpan2">
                <span>{t("patients.notes")}</span>
                <textarea
                  className="textarea"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={6}
                />
              </label>
              {/* Invoicing buyer data — required only when issuing invoices */}
              <div className="colSpan2" style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-muted)", borderBottom: "1px solid var(--color-border)", paddingBottom: 6 }}>
                {t("patients.billingTitle")}
              </div>
              <label className="field">
                <span>{t("patients.billingName")}</span>
                <input className="input" value={form.billing_name}
                  placeholder={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, billing_name: e.target.value }))} />
              </label>
              <label className="field">
                <span>{t("patients.billingZip")}</span>
                <input className="input" value={form.billing_zip}
                  onChange={(e) => setForm((f) => ({ ...f, billing_zip: e.target.value }))} />
              </label>
              <label className="field">
                <span>{t("patients.billingCity")}</span>
                <input className="input" value={form.billing_city}
                  onChange={(e) => setForm((f) => ({ ...f, billing_city: e.target.value }))} />
              </label>
              <label className="field">
                <span>{t("patients.billingAddress")}</span>
                <input className="input" value={form.billing_address}
                  onChange={(e) => setForm((f) => ({ ...f, billing_address: e.target.value }))} />
              </label>
              <label className="field">
                <span>{t("patients.billingTaxNumber")}</span>
                <input className="input" value={form.billing_tax_number}
                  onChange={(e) => setForm((f) => ({ ...f, billing_tax_number: e.target.value }))} />
              </label>
              <div className="formActions">
                <button className="btn btn--primary" type="submit" disabled={saving}>
                  {saving ? t("patients.saving") : t("patients.saveChanges")}
                </button>
                <button className="btn btn--ghost" type="button" onClick={() => setForm(emptyForm)}>
                  {t("common.close")}
                </button>
              </div>
            </form>

            {/* Data processing consent — record/withdraw attestations + history.
                Lives with the client record (moved here from the Trends page). */}
            <div style={{ padding: "16px 20px", borderTop: "1px solid var(--color-border)" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-title)" }}>
                {t("consent.dialogTitle")}
              </div>
              <ConsentBadge patient={form.patient_id!} patientName={form.name} />
            </div>

            {/* Connectors */}
            <div style={{ padding: "16px 20px", borderTop: "1px solid var(--color-border)" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-title)" }}>
                {t("patients.connectors")}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10 }}>
              <OuraConnectButton patientId={form.patient_id!} />
              <InviteToAppButton
                patientId={form.patient_id!}
                email={form.email}
                clientStatus={patients.find((p) => p.patient_id === form.patient_id)?.client_status}
                onStatusChange={(status) => {
                  setPatients((ps) =>
                    ps.map((p) =>
                      p.patient_id === form.patient_id
                        ? { ...p, client_status: status }
                        : p
                    )
                  );
                }}
              />
              <WipWearableConnectors />
              </div>
            </div>
          </section>
        )}
      </div>

      {/* NEW PATIENT MODAL */}
      {showNewModal && (
        <NewPatientModal
          onClose={() => setShowNewModal(false)}
          onCreate={createPatient}
          saving={saving}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t("patients.deletePatient")}
        message={t("patients.confirmDelete", { name: deleteTarget?.name })}
        confirmLabel={t("common.delete")}
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default PatientsPanel;

/* ================= Modal ================= */

const NewPatientModal: React.FC<{
  onClose: () => void;
  onCreate: (data: Omit<FormState, "patient_id">) => Promise<void>;
  saving: boolean;
}> = ({ onClose, onCreate, saving }) => {
  const { t } = useTranslation();
  // local state — fully separate from EDIT form
  const [newForm, setNewForm] = useState<Omit<FormState, "patient_id">>(
    // billing fields exist on the type but are entered later, on the edit
    // panel — the create modal stays lean.
    { ...emptyForm },
  );

  const backdropRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const onBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onCreate(newForm);
  };

  return (
    <div className="modalBackdrop" ref={backdropRef} onMouseDown={onBackdropClick}>
      <div className="modalCard" role="dialog" aria-modal="true" aria-label={t("patients.newPatient")}>
        <div className="modalHeader">
          <h3>{t("patients.newPatient")}</h3>
          <button className="iconBtn" onClick={onClose} aria-label={t("common.close")}>✕</button>
        </div>

        <form onSubmit={submit} className="formGrid">
          <label className="field">
            <span>{t("patients.name")}</span>
            <input
              className="input"
              value={newForm.name}
              onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span>{t("patients.email")}</span>
            <input
              className="input"
              value={newForm.email}
              onChange={(e) => setNewForm((f) => ({ ...f, email: e.target.value }))}
              type="email"
            />
          </label>
          <label className="field">
            <span>{t("patients.phone")}</span>
            <input
              className="input"
              value={newForm.phone}
              onChange={(e) => setNewForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>{t("patients.dob")}</span>
            <input
              className="input"
              value={newForm.dob}
              onChange={(e) => setNewForm((f) => ({ ...f, dob: e.target.value }))}
              placeholder={t("patients.dobPlaceholder")}
            />
          </label>
          <label className="field colSpan2">
            <span>{t("patients.notes")}</span>
            <textarea
              className="textarea"
              value={newForm.notes}
              onChange={(e) => setNewForm((f) => ({ ...f, notes: e.target.value }))}
              rows={6}
            />
          </label>

          <div className="formActions">
            <button className="btn btn--primary" type="submit" disabled={saving}>
              {saving ? t("patients.creating") : t("patients.create")}
            </button>
            <button className="btn btn--ghost" type="button" onClick={onClose}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
